"use client";

import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    type ReactNode,
} from "react";
import { fetchIpGeolocationInfo, type IpGeolocationInfo } from "@/lib/ip-geolocation";
import { reverseGeocode } from "@/lib/reverse-geocode";
import { FIRST_VISIT_TUTORIAL_DISMISSED_EVENT, FIRST_VISIT_TUTORIAL_SEEN_KEY } from "@/lib/first-visit-tutorial";

const STORAGE_KEY = "aurora-location-manual";

const DEFAULT_LAT = -43.5301;
const DEFAULT_LON = 172.6869;

export interface LocationCoordinates {
    lat: number;
    lon: number;
    accuracy?: number;
}

export type LocationSource = "geolocation" | "manual" | "ip-fallback" | "default";

export interface LocationState {
    coordinates: LocationCoordinates | null;
    placeName: string | null;
    detectedCountry: IpGeolocationInfo | null;
    error: string | null;
    loading: boolean;
    source: LocationSource;
    isApproximate: boolean;
}

interface LocationContextValue extends LocationState {
    effectiveCoordinates: LocationCoordinates;
    requestPermission: () => void;
    setManualLocation: (lat: number, lon: number, placeName?: string) => void;
    applyIpFallbackLocation: () => Promise<void>;
    clearManualLocation: () => void;
    hasLocation: boolean;
    needsCitySelection: boolean;
    locationDialogOpen: boolean;
    setLocationDialogOpen: (open: boolean) => void;
}

const LocationContext = createContext<LocationContextValue | null>(null);

type StoredManualLocation = {
    lat: number;
    lon: number;
    name?: string;
    source?: "manual" | "ip-fallback";
};

function loadManualFromStorage(): StoredManualLocation | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredManualLocation;
        if (
            typeof parsed?.lat === "number" &&
            typeof parsed?.lon === "number" &&
            parsed.lat >= -90 &&
            parsed.lat <= 90 &&
            parsed.lon >= -180 &&
            parsed.lon <= 180
        ) {
            return parsed;
        }
    } catch {
        // ignore
    }
    return null;
}

function saveManualToStorage(coords: StoredManualLocation | null) {
    if (typeof window === "undefined") return;
    try {
        if (coords) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(coords));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // ignore
    }
}

function shouldDeferAutomaticLocationDialog() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(FIRST_VISIT_TUTORIAL_SEEN_KEY) !== "true";
    } catch {
        return false;
    }
}

export function useLocation() {
    const ctx = useContext(LocationContext);
    if (!ctx) {
        throw new Error("useLocation must be used within LocationProvider");
    }
    return ctx;
}

export function LocationProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<LocationState>({
        coordinates: null,
        placeName: null,
        detectedCountry: null,
        error: null,
        loading: true,
        source: "default",
        isApproximate: false,
    });
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const hasAutoOpenedRef = useRef(false);
    const placeNameRequestIdRef = useRef(0);
    const ipFallbackRef = useRef<IpGeolocationInfo | null>(null);

    const openCitySelectionDialog = useCallback(() => {
        if (shouldDeferAutomaticLocationDialog()) {
            return;
        }
        if (!hasAutoOpenedRef.current) {
            hasAutoOpenedRef.current = true;
        }
        setLocationDialogOpen(true);
    }, []);

    const resolvePlaceName = useCallback(async (lat: number, lon: number) => {
        const requestId = ++placeNameRequestIdRef.current;
        const placeName = await reverseGeocode(lat, lon);
        if (requestId !== placeNameRequestIdRef.current) return;

        setState((prev) => {
            if (!prev.coordinates) return prev;
            if (prev.coordinates.lat !== lat || prev.coordinates.lon !== lon) return prev;
            return { ...prev, placeName };
        });
    }, []);

    const detectCountryFromIp = useCallback(async () => {
        const info = await fetchIpGeolocationInfo();
        ipFallbackRef.current = info;
        setState((prev) => ({
            ...prev,
            detectedCountry: info,
            loading: false,
            source: "default",
            coordinates: null,
            placeName: null,
            isApproximate: false,
            error: info
                ? null
                : "Location access denied. Select your city below or enable GPS.",
        }));
        openCitySelectionDialog();
    }, [openCitySelectionDialog]);

    const applyIpFallbackLocation = useCallback(async () => {
        let info = ipFallbackRef.current;
        if (!info) {
            info = await fetchIpGeolocationInfo();
            ipFallbackRef.current = info;
        }
        if (!info) {
            setState((prev) => ({
                ...prev,
                error: "Could not determine an approximate location from your connection.",
            }));
            return;
        }

        const placeName = info.city ?? info.country;
        const coords: LocationCoordinates = {
            lat: info.lat,
            lon: info.lon,
            accuracy: 50000,
        };

        saveManualToStorage({
            lat: coords.lat,
            lon: coords.lon,
            name: placeName,
            source: "ip-fallback",
        });

        setState({
            coordinates: coords,
            placeName,
            detectedCountry: info,
            error: null,
            loading: false,
            source: "ip-fallback",
            isApproximate: true,
        });
    }, []);

    const requestGeolocation = useCallback(() => {
        if (!("geolocation" in navigator)) {
            void detectCountryFromIp();
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coords: LocationCoordinates = {
                    lat: parseFloat(position.coords.latitude.toFixed(3)),
                    lon: parseFloat(position.coords.longitude.toFixed(3)),
                    accuracy: position.coords.accuracy ? position.coords.accuracy + 15 : undefined,
                };
                setState({
                    coordinates: coords,
                    placeName: null,
                    detectedCountry: null,
                    error: null,
                    loading: false,
                    source: "geolocation",
                    isApproximate: false,
                });
                saveManualToStorage(null);
                void resolvePlaceName(coords.lat, coords.lon);
            },
            () => {
                void detectCountryFromIp();
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000,
            }
        );
    }, [detectCountryFromIp, resolvePlaceName]);

    const requestPermission = useCallback(() => {
        saveManualToStorage(null);
        hasAutoOpenedRef.current = false;
        requestGeolocation();
    }, [requestGeolocation]);

    const setManualLocation = useCallback((lat: number, lon: number, placeName?: string) => {
        const coords: LocationCoordinates = {
            lat: parseFloat(Number(lat).toFixed(3)),
            lon: parseFloat(Number(lon).toFixed(3)),
        };
        saveManualToStorage({
            lat: coords.lat,
            lon: coords.lon,
            name: placeName,
            source: "manual",
        });
        setState({
            coordinates: coords,
            placeName: placeName ?? null,
            detectedCountry: null,
            error: null,
            loading: false,
            source: "manual",
            isApproximate: false,
        });
        if (!placeName) {
            void resolvePlaceName(coords.lat, coords.lon);
        }
    }, [resolvePlaceName]);

    const clearManualLocation = useCallback(() => {
        saveManualToStorage(null);
        requestGeolocation();
    }, [requestGeolocation]);

    useEffect(() => {
        const manual = loadManualFromStorage();
        if (manual) {
            const isApproximate = manual.source === "ip-fallback";
            setState({
                coordinates: { lat: manual.lat, lon: manual.lon },
                placeName: manual.name ?? null,
                detectedCountry: null,
                error: null,
                loading: false,
                source: isApproximate ? "ip-fallback" : "manual",
                isApproximate,
            });
            if (!manual.name) {
                void resolvePlaceName(manual.lat, manual.lon);
            }
            return;
        }
        requestGeolocation();
    }, [requestGeolocation, resolvePlaceName]);

    useEffect(() => {
        const handleTutorialDismissed = () => {
            if (state.loading || state.source !== "default") return;
            openCitySelectionDialog();
        };

        window.addEventListener(FIRST_VISIT_TUTORIAL_DISMISSED_EVENT, handleTutorialDismissed);
        return () => window.removeEventListener(FIRST_VISIT_TUTORIAL_DISMISSED_EVENT, handleTutorialDismissed);
    }, [openCitySelectionDialog, state.loading, state.source]);

    const effectiveCoordinates = useMemo((): LocationCoordinates => {
        if (state.coordinates) {
            return state.coordinates;
        }
        return { lat: DEFAULT_LAT, lon: DEFAULT_LON };
    }, [state.coordinates]);

    const hasLocation = state.source !== "default";
    const needsCitySelection = !hasLocation && !state.loading;

    const value = useMemo<LocationContextValue>(
        () => ({
            ...state,
            effectiveCoordinates,
            requestPermission,
            setManualLocation,
            applyIpFallbackLocation,
            clearManualLocation,
            hasLocation,
            needsCitySelection,
            locationDialogOpen,
            setLocationDialogOpen,
        }),
        [
            state,
            effectiveCoordinates,
            requestPermission,
            setManualLocation,
            applyIpFallbackLocation,
            clearManualLocation,
            hasLocation,
            needsCitySelection,
            locationDialogOpen,
        ]
    );

    return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}
